import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Fetches active admin-managed banners for the ForkHire platform,
 * filtered by target scope and user's org.
 */
export function useAdminBanners(platform: 'hire' | 'seek' = 'hire') {
  const { profile, session } = useAuth();
  const orgId = profile?.org_id;
  const userId = session?.user?.id;

  return useQuery({
    queryKey: ['admin-banners-active', platform, orgId],
    queryFn: async () => {
      // Fetch published banners for this platform
      const { data: banners, error } = await supabase
        .from('admin_banners')
        .select('*')
        .eq('status', 'published')
        .in('target_platform', [platform, 'both'])
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error || !banners) return { banners: [], overrideSystem: false };

      // Filter by date range client-side
      const now = new Date();
      const active = banners.filter(b => {
        if (b.starts_at && new Date(b.starts_at) > now) return false;
        if (b.expires_at && new Date(b.expires_at) <= now) return false;
        // Filter by target scope
        if (b.target_scope === 'org' && b.target_org_id && b.target_org_id !== orgId) return false;
        return true;
      });

      // Fetch user's dismissals
      let dismissedIds = new Set<string>();
      if (userId) {
        const { data: dismissals } = await supabase
          .from('admin_banner_dismissals')
          .select('banner_id')
          .eq('user_id', userId);
        dismissedIds = new Set((dismissals || []).map(d => d.banner_id));
      }

      const visible = active.filter(b => !dismissedIds.has(b.id));
      const overrideSystem = visible.some(b => b.override_system_banners);

      return { banners: visible, overrideSystem };
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });
}
